import { FirebaseApp } from 'firebase/app'
import { Auth, getAuth, GoogleAuthProvider, signInWithPopup } from 'firebase/auth'
import { AuthEventChannel } from '@tritt/firebase-client/lib/types/auth/events/authEventChannel'
import { AuthResultType, AuthService, AuthState, IdTokenProvider } from '@tritt/firebase-api'
import { AuthServices } from '@tritt/firebase-client'
import { AuthAdapter } from './auth-adapter.interface'
import { signInUrl } from '../patron.service'

/**
 * Real auth adapter using Conta/Tritt Firebase SDK.
 * This is the production implementation.
 */
export class ContaAuthAdapter implements AuthAdapter {
    private app: FirebaseApp | null = null
    private authServices: AuthServices | null = null
    private authService: AuthService | null = null
    private auth: Auth | null = null
    private googleAuthProvider: GoogleAuthProvider | null = null
    private tokenProvider: IdTokenProvider | undefined = undefined
    private authState: AuthState | null = null
    private listeners: Set<(user: AuthState | null) => void> = new Set()

    /**
     * Set the Firebase app and auth services.
     * Must be called before init().
     */
    configure(app: FirebaseApp, authServices: AuthServices): void {
        this.app = app
        this.authServices = authServices
    }

    async init(): Promise<void> {
        if (!this.app || !this.authServices) {
            throw new Error('[ContaAuthAdapter] configure() must be called before init()')
        }

        this.authService = this.authServices.service
        const authEvents: AuthEventChannel = this.authServices.eventChannel

        // Get SSO session
        const res = await this.authService.getSingleSignOnSession()
        this.authState = res || null

        // Check if we need to redirect to Patron
        // @ts-ignore
        const shouldSkipRedirect = window.Cypress || window.location.host.includes('tritt.test')
        if (!this.authState && !shouldSkipRedirect) {
            window.location.href = signInUrl
            return
        }

        this.notifyListeners()
        this.auth = getAuth(this.app)
        this.googleAuthProvider = new GoogleAuthProvider()

        // Setup auth event listeners
        authEvents.addOnLoginListener(({ user }) => {
            this.authState = user
            this.notifyListeners()
        })

        authEvents.addOnLogoutListener(() => {
            this.authState = null
            this.notifyListeners()
        })

        this.tokenProvider = this.authServices.tokenProvider

        // Expose Cypress helpers for real-backend tests
        // @ts-ignore
        if (window.Cypress) {
            // @ts-ignore
            window.fbSignIn = (username: string, password: string) => {
                return this.login(username, password)
            }
            // @ts-ignore
            window.fbLogout = () => this.logout()
            // @ts-ignore
            window.userId = this.authState?.uid
        }
    }

    async login(email: string, password: string): Promise<void> {
        if (!this.authService) {
            throw new Error('[ContaAuthAdapter] Not initialized')
        }

        await this.authService.login({
            client: 'pracsys',
            resultType: AuthResultType.Token,
            username: email,
            password
        })
        // Auth state will be updated via the login listener
    }

    async logout(): Promise<void> {
        if (!this.authService) {
            throw new Error('[ContaAuthAdapter] Not initialized')
        }

        try {
            await this.authService.logout()
        } catch (e) {
            console.error('[ContaAuthAdapter] Logout error:', e)
        }
    }

    getAuthState(): AuthState | null {
        return this.authState
    }

    async getAccessToken(): Promise<string | undefined> {
        return this.tokenProvider?.getIdToken()
    }

    onAuthStateChange(callback: (user: AuthState | null) => void): () => void {
        this.listeners.add(callback)

        // Immediately call with current state
        callback(this.authState)

        return () => {
            this.listeners.delete(callback)
        }
    }

    private notifyListeners(): void {
        this.listeners.forEach(cb => cb(this.authState))
    }

    /**
     * Google popup sign-in (used for specific flows).
     */
    async signInWithGooglePopup(): Promise<void> {
        if (!this.auth || !this.googleAuthProvider) {
            throw new Error('[ContaAuthAdapter] Not initialized')
        }

        try {
            await signInWithPopup(this.auth, this.googleAuthProvider)
        } catch (error) {
            console.error('[ContaAuthAdapter] Google sign-in error:', error)
        }
    }

    /**
     * Force refresh the ID token.
     */
    async forceRefreshToken(): Promise<void> {
        await this.auth?.currentUser?.getIdToken(true)
    }
}
